/**
 * Data Provider Abstraction Layer
 * 
 * This module provides an abstraction over data operations to support:
 * - Online Mode: Uses Supabase (PostgreSQL, Realtime, Storage, Auth)
 * - Standalone Mode: Uses IndexedDB, BroadcastChannel, localStorage
 * 
 * 📖 Adding a new table? Read: docs/data-provider-guide.md
 */

// Query options for filtering, sorting, and pagination
export interface QueryOptions {
  filters?: QueryFilter[]
  orderBy?: { column: string; ascending?: boolean }[]
  limit?: number
  offset?: number
  select?: string // For related data selection (e.g., 'id, name, parent:parent_id(*)')
  includeDeleted?: boolean // If true, include soft-deleted records (deleted_at IS NOT NULL)
}

export interface QueryFilter {
  column: string
  operator: FilterOperator
  value: any
}

export type FilterOperator = 
  | 'eq'      // equals
  | 'neq'     // not equals
  | 'gt'      // greater than
  | 'gte'     // greater than or equal
  | 'lt'      // less than
  | 'lte'     // less than or equal
  | 'like'    // pattern matching
  | 'ilike'   // case-insensitive pattern matching
  | 'in'      // in array
  | 'is'      // is null/not null
  | 'contains' // array contains

// Change payload for realtime subscriptions
export interface ChangePayload<T = any> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  table: string
  old: T | null
  new: T | null
  errors: string[] | null
}

// Subscription handle
export interface Subscription {
  unsubscribe: () => void | Promise<void>
}

// Storage file metadata
export interface FileMetadata {
  path: string
  size: number
  contentType: string
  createdAt: string
  updatedAt: string
}

// User identity for standalone mode
export interface StandaloneUser {
  id: string
  email: string | null
  username: string | null
  createdAt: string
}

// Auth state
export interface AuthState {
  user: StandaloneUser | null
  isAuthenticated: boolean
}

// Result types for operations
export interface OperationResult<T = void> {
  success: boolean
  data?: T
  error?: string
}

/**
 * Main Data Provider Interface
 * 
 * All stores should use this interface instead of calling Supabase directly.
 * This allows seamless switching between Online and Standalone modes.
 */
export interface DataProvider {
  // ==================== CRUD Operations ====================
  
  /**
   * Query records from a table with optional filters, sorting, and pagination
   */
  query<T>(table: string, options?: QueryOptions): Promise<T[]>
  
  /**
   * Get a single record by ID
   */
  getById<T>(table: string, id: string): Promise<T | null>
  
  /**
   * Insert a new record
   */
  insert<T>(table: string, data: Partial<T>): Promise<OperationResult<T>>
  
  /**
   * Insert multiple records
   */
  insertMany<T>(table: string, data: Partial<T>[]): Promise<OperationResult<T[]>>
  
  /**
   * Update a record by ID
   */
  update<T>(table: string, id: string, data: Partial<T>): Promise<OperationResult<T>>
  
  /**
   * Update multiple records matching filters
   */
  updateMany<T>(table: string, filters: QueryFilter[], data: Partial<T>): Promise<OperationResult<number>>
  
  /**
   * Upsert a record (insert or update based on conflict)
   */
  upsert<T>(table: string, data: Partial<T>, onConflict?: string): Promise<OperationResult<T>>
  
  /**
   * Soft delete a record by ID (sets deleted_at timestamp)
   * Record can be restored later with restore()
   * @param deletionBatchId - Optional batch ID to group related deletions for batch restore
   *   Use 'import_xxx' for import operations, 'cascade_xxx' for parent-child deletes
   */
  delete(table: string, id: string, deletionBatchId?: string): Promise<OperationResult>
  
  /**
   * Soft delete multiple records matching filters
   * @param deletionBatchId - Optional batch ID to group related deletions for batch restore
   */
  deleteMany(table: string, filters: QueryFilter[], deletionBatchId?: string): Promise<OperationResult<number>>
  
  /**
   * Permanently delete a record by ID (cannot be undone)
   * Use this for emptying trash or when you really need to remove data
   */
  hardDelete(table: string, id: string): Promise<OperationResult>
  
  /**
   * Restore a soft-deleted record (clears deleted_at and deletion_batch_id)
   */
  restore(table: string, id: string): Promise<OperationResult>
  
  /**
   * Restore multiple soft-deleted records matching filters
   */
  restoreMany(table: string, filters: QueryFilter[]): Promise<OperationResult<number>>
  
  /**
   * Restore all records in a deletion batch
   */
  restoreBatch(batchId: string): Promise<OperationResult<number>>
  
  // ==================== Realtime / Cross-tab Sync ====================
  
  /**
   * Subscribe to changes on a table
   */
  subscribe<T>(
    table: string, 
    callback: (payload: ChangePayload<T>) => void,
    filters?: QueryFilter[]
  ): Subscription
  
  /**
   * Subscribe to a broadcast channel for custom events
   */
  subscribeToChannel(
    channelName: string,
    eventHandlers: Record<string, (payload: any) => void>
  ): Subscription
  
  /**
   * Broadcast an event to a channel
   */
  broadcast(channel: string, event: string, payload: any): Promise<void>
  
  // ==================== File Storage ====================
  
  /**
   * Upload a file to storage
   * Returns the storage path (not the public URL)
   */
  uploadFile(bucket: string, path: string, file: Blob | File): Promise<OperationResult<string>>
  
  /**
   * Get a signed/accessible URL for a file
   */
  getFileUrl(bucket: string, path: string, expiresIn?: number): Promise<string | null>
  
  /**
   * Delete a file from storage
   */
  deleteFile(bucket: string, path: string): Promise<OperationResult>
  
  /**
   * List files in a storage path
   */
  listFiles(bucket: string, path: string): Promise<FileMetadata[]>
  
  // ==================== Auth ====================
  
  /**
   * Get current auth state
   */
  getAuthState(): AuthState
  
  /**
   * Get current user ID (required for data scoping)
   */
  getCurrentUserId(): string | null
  
  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (state: AuthState) => void): Subscription
  
  // ==================== Provider Info ====================
  
  /**
   * Get the provider mode
   */
  getMode(): 'online' | 'standalone'
  
  /**
   * Check if provider is ready/initialized
   */
  isReady(): boolean
  
  /**
   * Initialize the provider (called on app startup)
   */
  initialize(): Promise<void>
  
  /**
   * Cleanup resources (called on app shutdown)
   */
  cleanup(): Promise<void>
}

/**
 * Storage bucket names used in the app
 */
export const STORAGE_BUCKETS = {
  USER_IMAGES: 'user-images',
  USER_AUDIO: 'user-audio',
  AUDIO_FILES: 'audio-files'
} as const

export type StorageBucket = typeof STORAGE_BUCKETS[keyof typeof STORAGE_BUCKETS]

/**
 * Table names used in the app
 * 
 * Note: Only include tables that exist in BOTH Supabase AND standalone mode.
 * If a table only exists in one mode, handle it separately.
 */
export const TABLES = {
  SCENES: 'scenes',
  DISPLAYS: 'displays',
  DISPLAY_LAYERS: 'display_layers',
  DISPLAY_LAYER_OVERRIDES: 'display_layer_overrides',
  SCENE_DISPLAY_CONFIGS: 'scene_display_configs',
  IMAGE_LIBRARY: 'image_library',
  // Note: image_library_folders was removed - it doesn't exist in Supabase
  AUDIO_LIBRARY: 'audio_library',
  AUDIO_LIBRARY_FOLDERS: 'audio_library_folders',
  AUDIO_LIBRARY_FILES: 'audio_library_files',
  MUSIC_NODES: 'music_nodes',
  SPOTIFY_NODES: 'spotify_nodes',
  PLAYLIST_NODES: 'playlist_nodes',
  SCENE_MUSIC_CONFIGS: 'scene_music_configs',
  SCENE_SPOTIFY_CONFIGS: 'scene_spotify_configs',
  SCENE_PLAYLIST_CONFIGS: 'scene_playlist_configs',
  SCENE_LIGHT_CONFIGS: 'scene_light_configs',
  HUE_BRIDGES: 'hue_bridges',
  LIGHTS: 'lights',
  WLED_DEVICES: 'wled_devices',
  WLED_OUTPUTS: 'wled_outputs',
  GRAPH_CONTAINERS: 'graph_containers',
  SPOTIFY_INTEGRATIONS: 'spotify_integrations',
  HUE_SYNC_SESSIONS: 'hue_sync_sessions',
  CHARACTERS: 'characters',
  GAME_ROOMS: 'game_rooms'
} as const

export type TableName = typeof TABLES[keyof typeof TABLES]
